/* eslint-disable no-console */
import { UserPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import {
  UpdateUser,
  UpdateUserAccessRequest,
  USER_ROLE,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { AuthorizationPromiseClient } from '@trustero/trustero-api-web/lib/authz/authz_grpc_web_pb'
import {
  GetAccessRolesRequest,
  GetAccessRolesResponse,
} from '@trustero/trustero-api-web/lib/authz/authz_pb'
import { StringList } from '@trustero/trustero-api-web/lib/common/collections_pb'
import log from 'loglevel'
import { useCallback } from 'react'
import { useAuthorizedGrpcClientWithContentUpdate } from 'src/adapter/grpcClient'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useUsers } from 'src/components/async/Users/useUsers'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { showInfoToast } from 'src/Utils/helpers/toast'

export const useUpdateUserRole = (): ((
  role: USER_ROLE,
  userId: string,
  auditIds?: string[],
  hasQc?: boolean,
) => Promise<void>) => {
  const { mutate } = useUsers()
  const userClient = useAuthorizedGrpcClientWithContentUpdate(UserPromiseClient)

  return useCallback(
    async (
      role: USER_ROLE,
      userId: string,
      auditIds?: string[],
      hasQc?: boolean,
    ) => {
      const request = new UpdateUser().setRole(role).setId(userId)

      if (role === USER_ROLE.AUDITOR) {
        request.setAuditIds(new StringList().setItemList(auditIds || []))
        request.setAllowQc(!!hasQc)
      }

      try {
        await userClient.update(request)
        await mutate()
      } catch (error) {
        log.error(error)
        showInfoToast('Failed to update user role')
      }
    },
    [mutate, userClient],
  )
}

export const useAccessRoles = (
  includeNoAccess?: boolean,
): GrpcResponse<GetAccessRolesResponse> => {
  const request = new GetAccessRolesRequest()
  if (includeNoAccess !== undefined) {
    request.setIncludeNoAccess(includeNoAccess)
  }
  const { response } = useSwrImmutableGrpc(
    AuthorizationPromiseClient.prototype.getAccessRoles,
    request,
  )
  return NewGrpcResponse(response)
}

export const useUpdateUserAccess = (): ((
  accessRoleId: string,
  userId: string,
  auditIds?: string[],
) => Promise<void>) => {
  const { mutate } = useUsers()
  const userClient = useAuthorizedGrpcClientWithContentUpdate(UserPromiseClient)
  return useCallback(
    async (accessRoleId: string, userId: string, auditIds?: string[]) => {
      try {
        const request = new UpdateUserAccessRequest()
          .setUserId(userId)
          .setAccessRoleId(accessRoleId)
        if (auditIds) {
          request.setAuditIds(new StringList().setItemList(auditIds))
        }
        await userClient.updateUserAccess(request)
        await mutate()
      } catch (error) {
        log.error(error)
        showInfoToast('Failed to update user access')
      }
    },
    [userClient, mutate],
  )
}

export const useUpdateUserName = (): ((
  name: string,
  userId: string,
) => Promise<boolean>) => {
  const { mutate } = useUsers()
  const userClient = useAuthorizedGrpcClientWithContentUpdate(UserPromiseClient)

  return useCallback(
    async (name: string, userId: string) => {
      const request = new UpdateUser().setName(name).setId(userId)
      try {
        await userClient.update(request)
        await mutate()
        return true
      } catch (error) {
        log.error(error)
        showInfoToast('Failed to update user name')
        return false
      }
    },
    [mutate, userClient],
  )
}

export const useUpdateUserDepartment = (): ((
  departmentId: string,
  userId: string,
) => Promise<boolean>) => {
  const { mutate } = useUsers()
  const userClient = useAuthorizedGrpcClientWithContentUpdate(UserPromiseClient)

  return useCallback(
    async (departmentId: string, userId: string) => {
      const request = new UpdateUser().setDepartment(departmentId).setId(userId)
      try {
        await userClient.update(request)
        await mutate()
        return true
      } catch (error) {
        log.error(error)
        showInfoToast('Failed to update user department')
        return false
      }
    },
    [mutate, userClient],
  )
}
